import { fields } from "@affinity/Overrides/Widgets/Basic/Banner";
import { iconVariants } from "@affinity/Overrides/Widgets/Basic/Button";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import FieldDefinition from "@insite/client-framework/Types/FieldDefinition";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link from "@insite/mobius/Link";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import React from "react";
import { css } from "styled-components";

export interface CTAProps {
    [fields.label]: string;
    [fields.link]: LinkFieldValue;
    [fields.variant]: "primary" | "secondary" | "tertiary" | "link";
    [fields.iconLocation]: "left" | "right";
    [fields.icon]: typeof iconVariants[number]["value"];
}

export interface CTAGroupProps {
    cta?: { fields: CTAProps }[];
}

export const CTAGroup: React.FC<CTAGroupProps & { defaultProps?: Pick<ButtonPresentationProps, "sizeVariant"> }> = ({
    cta,
    defaultProps,
}) => {
    return (
        <>
            {cta?.map(({ fields }, index) => (
                <CTAButton {...fields} key={index} {...defaultProps} />
            ))}
        </>
    );
};

const CTAButton = (props: CTAProps & Pick<ButtonPresentationProps, "sizeVariant">) => {
    const { url, title } = useGetLink(props.link);
    const history = useHistory();
    const { isInShell } = useShellContext();

    const onClick = () => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    if (props.variant === "link") {
        return (
            <Link
                icon={{
                    iconProps: {
                        src: props.icon,
                    },
                    position: props.iconLocation,
                }}
                href={url}
                onClick={onClick}
                mergeCss={true}
                css={css`gap: 10px`}
            >
                {props.label || title || url}
            </Link>
        );
    }

    return (
        <Button
            variant={props.variant}
            icon={{ src: props.icon, position: props.iconLocation }}
            sizeVariant={props.sizeVariant || "large"}
            onClick={onClick}
        >
            {props.label || title || url}
        </Button>
    );
};

export const CTAGroupField: FieldDefinition = {
    displayName: "CTAs",
    name: "cta",
    editorTemplate: "ListField",
    getDisplay: ({ fields }) => {
        return fields.label;
    },
    defaultValue: [],
    fieldType: "General",
    validate: value => {
        if (value.length > 2) {
            return "You can only have a maximum of 2 CTAs.";
        }
        return null;
    },
    fieldDefinitions: [
        {
            name: "label",
            editorTemplate: "TextField",
            displayName: "Label",
            defaultValue: "",
            isRequired: true,
        },
        {
            name: "link",
            displayName: "Link",
            editorTemplate: "LinkField",
            defaultValue: { type: "Page", value: "" },
            isRequired: true,
        },
        {
            name: "variant",
            displayName: "Variant",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "Primary button", value: "primary" },
                { displayName: "Secondary button", value: "secondary" },
                { displayName: "Tertiary button", value: "tertiary" },
                { displayName: "Link", value: "link" },
            ],
            hideEmptyOption: true,
            defaultValue: "primary",
        },
        {
            name: "icon",
            displayName: "Icon",
            editorTemplate: "DropDownField",
            options: iconVariants,
            defaultValue: "",
        },
        {
            name: "iconLocation",
            displayName: "Icon Location",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "Left", value: "left" },
                { displayName: "Right", value: "right" },
            ],
            defaultValue: "left",
        },
    ],
};
